<template>
  <div>
    <slot name="filters"></slot>
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-gray-200">
            <th
              v-for="(column, colIndex) in columns"
              :key="`column.th.${column.key}`"
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap"
              :class="colIndex === 0 && stickyFirstColumn ? 'sticky left-0 z-10 bg-white' : ''"
            >
              <div class="flex group" :class="{ 'cursor-pointer': column.sortable !== false }">
                {{ column.label }}

                <div v-if="column.sortable !== false">
                  <a
                    v-if="sort.order === 'desc'"
                    @click="$emit('sort-updated', { key: column.key.toLowerCase(), order: 'asc' })"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      :class="sortClass(column.key)"
                      class="rounded-sm cursor-pointer group-hover:opacity-100 h-4 w-4 ml-2"
                      viewBox="0 0 24 24"
                      stroke-width="2.5"
                      stroke="currentColor"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="18" y1="13" x2="12" y2="19" />
                      <line x1="6" y1="13" x2="12" y2="19" />
                    </svg>
                  </a>
                  <a v-else @click="$emit('sort-updated', { key: column.key.toLowerCase(), order: 'desc' })">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      :class="sortClass(column.key)"
                      class="rounded-sm cursor-pointer group-hover:opacity-100 h-4 w-4 ml-2"
                      viewBox="0 0 24 24"
                      stroke-width="2.5"
                      stroke="currentColor"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="18" y1="11" x2="12" y2="5" />
                      <line x1="6" y1="11" x2="12" y2="5" />
                    </svg>
                  </a>
                </div>
              </div>
            </th>
            <th
              v-if="rowActions && rowActions.length > 0"
              class="w-16 px-4 py-3 text-left text-xs font-medium text-gray-500"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="`row.${index}`"
            class="group relative border-b border-gray-100 last:border-b-0 transition-colors hover:bg-gray-50"
            :class="index % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'"
            @click="handleClick(row)"
          >
            <td
              v-for="(column, colIndex) in columns"
              :key="`row.${index}.column.${column.key}`"
              class="px-4 py-3 whitespace-nowrap"
              :class="[
                { 'cursor-pointer': clickable || linkable },
                colIndex === 0 && stickyFirstColumn
                  ? `sticky left-0 z-10 group-hover:bg-gray-50 ${index % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`
                  : '',
              ]"
            >
              <a
                v-if="linkable && colIndex === 0"
                :href="linkFn(row)"
                class="absolute inset-0"
                @click.prevent="handleClick(row)"
              >
                <span class="sr-only">View {{ formatTableData(row[columns[0].key], columns[0]) }}</span>
              </a>
              <slot :name="`td-${column.key}`" :row="row" :column="column">
                {{ formatTableData(row[column.key], column) }}
              </slot>
            </td>
            <td v-if="rowActions && rowActions.length > 0" class="px-4 py-3 flex space-x-2" @click.stop>
              <button
                v-for="(action, actionIndex) in rowActions"
                :key="`row.${index}.action.${actionIndex}`"
                :title="action.tooltip"
                :class="action.class || ''"
                class="p-1 rounded-md hover:bg-gray-100"
                @click="action.action(row)"
              >
                <vue-feather :type="action.icon" class="h-4 w-4" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
/**
 * @deprecated Use `DataTable` from `@/components/shared/DataTable/data-table` instead.
 * Retained only for consumers that have not yet migrated to the shadcn-based DataTable family.
 */
export default {
  props: {
    columns: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
    filters: {
      type: Array,
      required: false,
      default: () => {
        return [];
      },
    },
    sort: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    clickFn: {
      type: Function,
      required: false,
    },
    linkFn: {
      type: Function,
      required: false,
    },
    stickyFirstColumn: {
      type: Boolean,
      required: false,
      default: false,
    },
    rowActions: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  computed: {
    clickable() {
      return typeof this.clickFn === 'function';
    },
    linkable() {
      return typeof this.linkFn === 'function';
    },
  },
  methods: {
    handleClick(row) {
      if (!this.clickable) return;
      this.clickFn(row);
    },
    sortClass(columnKey) {
      let matchedSortingName = !!this.sort.key && this.sort.key.toLowerCase() == columnKey.toLowerCase() ? true : false;
      return {
        'opacity-100': matchedSortingName,
        'opacity-0': !matchedSortingName,
      };
    },
    formatTableData(data, column) {
      if (!column.formatter || typeof column.formatter !== 'function') return data;

      return column.formatter(data);
    },
  },
};
</script>
